// src/components/account/AccountNew.jsx
import React, { useState } from 'react';
import Navbar from '../layout/Navbar';
import AddressTab from './tabs/AddressTab';
import ContactTab from './tabs/ContactTab';
import DestinationTab from './tabs/DestinationTab';
import DocumentTab from './tabs/DocumentTab';
import {
  ACCOUNT_CREATE_ENDPOINT,
  ACCOUNT_DUPLICATE_CHECK_ENDPOINT,
  GST_LOOKUP_ENDPOINT,
  GST_EMAIL,
  GST_API_HEADERS
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';
import WabaTab from './tabs/WabaTab';


function AccountNew() {
  const tabs = ['address', 'contact', 'destination', 'documents', 'waba'];
  const [activeTab, setActiveTab] = useState('address');
  const [accountExists, setAccountExists] = useState(false);
  const [waba, setWaba] = useState({ number: '', name: '' });


  const [form, setForm] = useState({
    account_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    mobile: '',
    email: '',
    gst_no: '',
    contacts: [{ contact_person: '', phone1: '', phone2: '', email: '', department: '' }],
    destinations: [{ destination_address: '', remark: '', destination_city: '' }],
    documents: []
  });

  
const setDocuments = (updatedDocs) => {
  setForm(prev => ({ ...prev, documents: updatedDocs }));
};

  const handleFetchGstData = async () => {
    if (!form.gst_no || form.gst_no.trim().length < 15) {
      alert('Please enter a valid GST number');
      return;
    }

    const query = `${GST_LOOKUP_ENDPOINT}?email=${encodeURIComponent(GST_EMAIL)}&gstin=${form.gst_no.trim()}`;

    try {
      const res = await fetch(query, { headers: GST_API_HEADERS });
      const data = await res.json();

      if (data.status_cd === "1" && data.data) {
        const gst = data.data;
        const addr = gst.pradr?.addr || {};

        setForm(prev => ({
          ...prev,
          account_name: gst.tradeNam || prev.account_name,
          address: `${addr.bno || ''}, ${addr.bnm || ''}, ${addr.flno || ''}, ${addr.st || ''}, ${addr.locality || ''}, ${addr.landMark || ''}`,
          city: addr.loc || prev.city,
          state: addr.stcd || prev.state,
          pincode: addr.pncd || prev.pincode,
          country: 'India'
        }));

        alert('GST data filled successfully');
      } else {
        alert(data.status_desc || 'Invalid GST or not found');
      }
    } catch (err) {
      console.error('Error fetching GST data:', err);
      alert('Server error while fetching GST data');
    }
  };

  const handleAccountNameBlur = async () => {
    const trimmedName = form.account_name.trim();
    if (trimmedName.length === 0) return;

    try {
      const res = await fetch(ACCOUNT_DUPLICATE_CHECK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_name: trimmedName })
      });

      const data = await res.json();
      setAccountExists(data.exists);
    } catch (err) {
      console.error('Error checking account name:', err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleContactChange = (index, field, value) => {
    const updated = [...form.contacts];
    updated[index][field] = value;
    setForm({ ...form, contacts: updated });
  };

  const handleDestinationChange = (index, field, value) => {
    const updated = [...form.destinations];
    updated[index][field] = value;
    setForm({ ...form, destinations: updated });
  };

  const handleDocumentChange = (index, field, value) => {
    const updated = [...form.documents];
    updated[index][field] = value;
    setForm({ ...form, documents: updated });
  };

  const addContact = () => {
    setForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { contact_person: '', phone1: '', phone2: '', email: '', department: '' }]
    }));
  };

  const removeContact = (index) => {
    const updated = [...form.contacts];
    updated.splice(index, 1);
    setForm({ ...form, contacts: updated });
  };

  const addDestination = () => {
    setForm(prev => ({
      ...prev,
      destinations: [...prev.destinations, { destination_address: '', remark: '', destination_city: '' }]
    }));
  };

  const removeDestination = (index) => {
    const updated = [...form.destinations];
    updated.splice(index, 1);
    setForm({ ...form, destinations: updated });
  };

  const addDocument = () => {
    setForm(prev => ({
      ...prev,
      documents: [...prev.documents, { document_name: '', mandatory: false }]
    }));
  };

  const removeDocument = (index) => {
    const updated = [...form.documents];
    updated.splice(index, 1);
    setForm({ ...form, documents: updated });
  };


const handleSubmit = async (e) => {
  e.preventDefault();

  const trimmedName = form.account_name.trim();
  if (trimmedName.length === 0) {
    alert("Account name is required");
    return;
  }

  // Check for duplicate again before submission
  try {
    const duplicateCheck = await fetch(ACCOUNT_DUPLICATE_CHECK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_name: trimmedName })
    });
    const duplicateData = await duplicateCheck.json();

    if (duplicateData.exists) {
      alert("Duplicate account name not allowed. Please use a unique name.");
      return;
    }
  } catch (err) {
    console.error("Error rechecking duplicate:", err);
    alert("Unable to verify account name. Try again.");
    return;
  }

  try {
    const encryptedUser = localStorage.getItem('suyog_user');
    if (!encryptedUser) return alert('User not authenticated');

    const { user_id, user_token } = decryptData(encryptedUser);

    const payload = {
      ...form,
      user_id,
      user_token,
      documents: form.documents.filter(doc => doc.id), // Only include documents with valid id
       waba: {
    ...waba, // contains number, name, access_user_ids
    access_user_ids: waba.access_user_ids || [] // ensure fallback if not selected
  }
    };

    const res = await fetch(ACCOUNT_CREATE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await res.json();

    if (res.ok && data.success) {
      alert("✅ Account created successfully!");
      // Optional: redirect or reset form
    } else {
      alert(data.error || '❌ Failed to create account');
    }
  } catch (err) {
    console.error('Submit error:', err);
    alert('❌ Server error occurred. Please try again.');
  }
};

  const currentIndex = tabs.indexOf(activeTab);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === tabs.length - 1;

  return (
    <>
      <Navbar />
      <div className="container mt-5">
        <div className="card shadow-lg border-0 rounded-4">
          <div className="card-header text-white rounded-top-4" style={{ background: 'linear-gradient(90deg, #007bff, #0056b3)' }}>
            <h4 className="mb-0">Add New Account</h4>
            <ul className="nav nav-tabs card-header-tabs mt-3">
              {tabs.map(tab => (
                <li className="nav-item" key={tab}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab ? 'active' : 'text-white-50'}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              {activeTab === 'address' && (
                <AddressTab
                  form={form}
                  handleChange={handleChange}
                  handleAccountNameBlur={handleAccountNameBlur}
                  accountExists={accountExists}
                  handleFetchGstData={handleFetchGstData}
                />
              )}
              {activeTab === 'contact' && (
                <ContactTab
                  contacts={form.contacts}
                  handleContactChange={handleContactChange}
                  addContact={addContact}
                  removeContact={removeContact}
                />
              )}
              {activeTab === 'destination' && (
                <DestinationTab
                  destinations={form.destinations}
                  handleDestinationChange={handleDestinationChange}
                  addDestination={addDestination}
                  removeDestination={removeDestination}
                />
              )}
              {activeTab === 'documents' && (
                <DocumentTab
    documents={form.documents}
    setDocuments={setDocuments}
  />
              )}

             {activeTab === 'waba' && (
  <WabaTab waba={waba} setWaba={setWaba} />
)}

              <div className="d-flex justify-content-between mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setActiveTab(tabs[currentIndex - 1])} disabled={isFirst}>
                  Previous
                </button>

                {!isLast && (
                  <button type="button" className="btn btn-primary" onClick={() => setActiveTab(tabs[currentIndex + 1])}>
                    Next
                  </button>
                )}

                {isLast && (
                  <button type="submit" className="btn btn-success">
                    Create Account
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AccountNew;
